import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useProducts } from '../hooks/useProducts';
import { useCategories } from '../hooks/useCategories';
import ProductGrid from '../components/ProductGrid';
import Filters from '../components/Filters';
import FilterChips from '../components/FilterChips';
import RecentlyViewed from '../components/RecentlyViewed';
import { Sparkles, TrendingUp } from 'lucide-react';
import { getCategoryUrl } from '../utils/url-helpers';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../config/translations';
import SEOHead from '../components/SEOHead';

function HomePage() {
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);

  const [filters, setFilters] = useState({
    limit: 20,
    offset: 0,
    sort: 'name'
  });

  const { data, isLoading, error } = useProducts(filters);
  const { data: categoriesData } = useCategories();

  const products = data?.data?.products || [];
  // Filter out categories with 0 products
  const categories = (categoriesData?.data?.categories || []).filter(cat => cat.productCount > 0);

  const handleFilterChange = (newFilters) => {
    setFilters({ ...newFilters, offset: 0 });
  };

  const handleClearFilters = () => {
    setFilters({
      limit: 20,
      offset: 0,
      sort: 'name'
    });
  };

  const handleRemoveFilter = (filterKey) => {
    const newFilters = { ...filters };
    delete newFilters[filterKey];
    setFilters({ ...newFilters, offset: 0 });
  };

  const featuredCategories = categories.slice(0, 6);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        fallback={{
          title: 'PremiumStore - Compare Prices from Top Vendors',
          description: 'Compare prices and find the best deals from multiple trusted vendors. Browse products across categories with the best prices.',
          canonicalUrl: window.location.origin + window.location.pathname
        }}
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="container-custom py-16">
          <div className="max-w-3xl">
            <div className="flex items-center space-x-2 mb-4">
              <Sparkles className="w-6 h-6" aria-hidden="true" />
              <span className="text-sm font-medium uppercase tracking-wide">Premium Marketplace</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {t('heroTitle')}
            </h1>
            <p className="text-lg text-primary-100">
              {t('heroSubtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Featured Categories */}
      {featuredCategories.length > 0 && (
        <div className="bg-white border-b border-gray-200">
          <div className="container-custom py-8">
            <div className="flex items-center space-x-2 mb-6">
              <TrendingUp className="w-5 h-5 text-primary-600" aria-hidden="true" />
              <h2 className="text-xl font-bold text-gray-900">{t('popularCategories')}</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
              {featuredCategories.map((category) => (
                <Link
                  key={category.slug || category.name}
                  to={getCategoryUrl(category)}
                  className="card p-4 text-center hover:shadow-lg transition-shadow block"
                >
                  <div className="text-2xl mb-2" aria-hidden="true">📦</div>
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {category.name}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {category.productCount} items
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="container-custom py-8">
        {/* Recently Viewed */}
        <div className="mb-8">
          <RecentlyViewed />
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="sticky top-20">
              <Filters
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
              />
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {filters.category ? `${t('productsIn')} ${filters.category}` : t('allProducts')}
              </h2>
            </div>

            {/* Filter Chips */}
            <FilterChips
              filters={filters}
              onRemoveFilter={handleRemoveFilter}
              productCount={products.length}
            />

            <ProductGrid
              products={products}
              isLoading={isLoading}
              error={error}
              emptyStateType={
                filters.category || filters.brand || filters.minPrice || filters.maxPrice
                  ? 'filtered'
                  : 'default'
              }
              hasActiveFilters={
                !!(filters.category || filters.brand || filters.minPrice || filters.maxPrice)
              }
              onClearFilters={handleClearFilters}
            />

            {/* Load More — offset-based pagination to avoid re-fetching previous items */}
            {products.length >= filters.limit && (
              <div className="mt-8 text-center">
                <button
                  onClick={() => setFilters({ ...filters, offset: (filters.offset || 0) + filters.limit })}
                  className="btn-primary"
                  aria-label={t('loadMoreProducts')}
                >
                  {t('loadMoreProducts')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default HomePage;
